'use client'

import * as React from 'react'

import { cn } from '../../lib/utils'
import { useEffect, useRef, useState } from 'react'
import { Textarea as OriginTextArea } from '../ui/textarea'

function Textarea({
  className,
  value: controlledValue,
  placeholder,
  onChange,
  onBlur,
  ...props
}: React.ComponentProps<'textarea'>) {
  const [focused, setFocused] = useState(false)
  const [uncontrolledValue, setUncontrolledValue] = useState('')
  const areaRef = useRef<HTMLTextAreaElement>(null)

  // Support both controlled and uncontrolled usage
  const value =
    controlledValue !== undefined ? controlledValue : uncontrolledValue

  useEffect(() => {
    if (controlledValue !== undefined) return
    if (areaRef.current) {
      setUncontrolledValue(areaRef.current.value)
    }
  }, [])

  const handleFocus = () => setFocused(true)
  const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    setFocused(false)
    onBlur?.(e)
  }
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (controlledValue === undefined) {
      setUncontrolledValue(e.target.value)
    }
    onChange?.(e)
  }

  const isFloating = focused || !!value

  return (
    <div className="relative">
      <OriginTextArea
        ref={areaRef}
        data-slot="textarea"
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className={cn(
          'rounded-lg border-gray-300 bg-white pt-5 focus-visible:ring-0',
          className
        )}
        {...props}
      />
      <label
        className={cn(
          'pointer-events-none absolute left-3 top-3 origin-left bg-transparent text-gray-500 transition-all duration-200',
          isFloating
            ? '-translate-y-2 scale-90 !p-0 text-xs'
            : 'translate-y-0 scale-100 pt-[7px] text-base'
        )}
        style={{
          background: isFloating ? 'white' : 'transparent',
          paddingLeft: isFloating ? '0.25rem' : undefined,
          paddingRight: isFloating ? '0.25rem' : undefined,
        }}
      >
        {placeholder}
      </label>
    </div>
  )
}

export { Textarea }
